import { Group } from '@mantine/core';
import { useApiGetUserIamRole, type Filter } from '@repo/api-codegen';
import { EntityType } from '@repo/common/enums/entityType';
import {
	Breadcrumbs,
	BreadcrumbsSkeleton,
	Button,
	Text,
} from '@repo/foundations';
import { find, isNil, lowerCase } from 'lodash-es';
import type { MouseEvent } from 'react';
import { memo, useCallback, useContext, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import {
	NavBarBreadcrumbWrapper,
	NavBarRightSideWrapper,
	NavBarWrapper,
} from '.';
import { useAuthUser, useQuestionsEnabled } from '../../../api';
import { useTemplateList } from '../../../api/hooks/templates';
import { useHomepageTeam } from '../../../pages/HomePage/hooks/useHomepageTeam';
import { useFeatureFlags } from '../../../utils/featureFlags';
import { entityModalStore } from '../../EntityModal/store';
import { NavBarContext } from '../../PrivateRoute/NavBarContext';
import { TemplatesButtonGroup } from '../../Templates/TemplatesButtonGroup';
import { ENTITY_LABEL_OVERRIDE, TEAM_CATALOG_PAGE_PATHS } from './constants';

const CREATE_AND_OPEN_DIRECTLY_ENTITIES = [
	EntityType.document,
	EntityType.dictionary_term,
	EntityType.glossary,
	EntityType.collection,
	EntityType.metric,
	EntityType.bi_dashboard,
];

// Helper function to synchronously check if a filter contains team filters
function hasTeamFilter(filter: Filter, teamId: string): boolean {
	if (!filter || !teamId || filter.operator === 'not') return false;

	// Check if this is a direct team filter
	if (
		filter.field === 'teams' &&
		filter.operator === 'in' &&
		Array.isArray(filter.value)
	) {
		return (filter.value as string[]).includes(teamId);
	}

	// Check nested operands recursively
	if (filter.operands && Array.isArray(filter.operands)) {
		return filter.operands.some((operand) => hasTeamFilter(operand, teamId));
	}

	return false;
}

function TeamCatalogPageNavBar() {
	const { teamId: currentTeamId } = useParams();
	const { user, isAdminUser, workspace } = useAuthUser();
	const { team, isTeamEditorUser } = useHomepageTeam();

	const page = find(TEAM_CATALOG_PAGE_PATHS, ({ pattern }) =>
		pattern.test(window.location.pathname)
	);

	const location = useLocation();

	const { biDashboardsEnabled } = useFeatureFlags();
	const questionsEnabled = useQuestionsEnabled();

	const showPageBreadcrumb = !TEAM_CATALOG_PAGE_PATHS[0].pattern.test(
		location.pathname
	);

	const context = useContext(NavBarContext);
	const leftNode = context?.leftNode;
	const rightNode = context?.rightNode;

	const navigate = useNavigate();

	const { data: userIamRole } = useApiGetUserIamRole({
		pathParams: {
			userId: user.id,
		},
	});

	const canUserUpdateEntityType = useMemo(() => {
		if (!workspace?.migrated_permission_v2) {
			return (
				isTeamEditorUser ||
				isAdminUser ||
				page?.entityType === EntityType.question
			);
		}

		if (!userIamRole || !page?.entityType) {
			return false;
		}

		const hasGlobalUpdatePermission =
			userIamRole.permissions.includes('Resources.Update');

		// Check for entity-specific create permissions
		const hasEntitySpecificPermission = (() => {
			switch (page.entityType) {
				case EntityType.question:
					return (
						userIamRole.permissions.includes('Questions.Create') ||
						userIamRole.permissions.includes('Questions.Update')
					);
				case EntityType.query:
					return userIamRole.permissions.includes('Queries.Write');
				case EntityType.policy:
					return (
						userIamRole.permissions.includes('Policies.Create') ||
						userIamRole.permissions.includes('Policies.Update')
					);
				case EntityType.lineage:
					return (
						userIamRole.permissions.includes('Lineage.Create') ||
						userIamRole.permissions.includes('Lineage.Update')
					);
				case EntityType.dictionary_term:
				case EntityType.glossary:
					return (
						userIamRole.permissions.includes('Properties.Create') ||
						userIamRole.permissions.includes('Properties.Update')
					);
				default:
					// For document, collection, metric, and other entity types
					return (
						userIamRole.permissions.includes('Resources.Create') ||
						userIamRole.permissions.includes('Resources.Update')
					);
			}
		})();

		if (
			!userIamRole.resources_filter_configs ||
			userIamRole.resources_filter_configs.length === 0
		) {
			return hasGlobalUpdatePermission || hasEntitySpecificPermission;
		}

		for (const filterConfig of userIamRole.resources_filter_configs) {
			const matchesEntityType = filterConfig.filters.some((filter) => {
				if (filter.operator === 'or' && filter.operands) {
					return filter.operands.some(
						(operand) =>
							operand.operator === 'exact' &&
							operand.field === 'native_type' &&
							operand.value === page.entityType
					);
				}
				return false;
			});

			const matchesTeamsFilter = filterConfig.filters.some((filter) =>
				hasTeamFilter(filter, team?.id ?? '')
			);

			// Check if entity type filter should hide create functionality
			const entityTypeForceHideCreate =
				matchesEntityType &&
				filterConfig.permissions.includes('Resources.Read') &&
				!filterConfig.permissions.includes('Resources.Update');

			// Check if teams filter should hide create functionality
			const teamsForceHideCreate =
				matchesTeamsFilter &&
				!filterConfig.permissions.includes('Resources.CreateInTeams') &&
				!filterConfig.permissions.includes('Resources.Create');

			// Apply create filters based on operator
			const shouldHideCreate =
				filterConfig.operator === 'and'
					? entityTypeForceHideCreate && teamsForceHideCreate
					: entityTypeForceHideCreate || teamsForceHideCreate;

			if (shouldHideCreate) {
				return false;
			}
		}
		return (
			hasGlobalUpdatePermission ||
			hasEntitySpecificPermission ||
			isTeamEditorUser ||
			isAdminUser
		);
	}, [
		userIamRole,
		page?.entityType,
		workspace?.migrated_permission_v2,
		isTeamEditorUser,
		isAdminUser,
		team?.id,
	]);

	const hasEntityType = !isNil(page?.entityType);
	const withTemplate = ![EntityType.metric, EntityType.bi_dashboard].includes(
		page?.entityType!
	);
	const mustQuickCreate = CREATE_AND_OPEN_DIRECTLY_ENTITIES.includes(
		page?.entityType!
	);

	const { data: defaultTemplates } = useTemplateList({
		filters: {
			is_default: true,
			entity_type_template: page?.entityType,
		},
		options: {
			enabled: hasEntityType && mustQuickCreate,
		},
	});

	const crumbs = useMemo(() => {
		const teamCrumb = {
			title: team?.name ?? '',
			icon: team?.icon || undefined,
			href: `/teams/${team?.id}`,
		};

		const pageCrumb = {
			title: page?.name ?? '',
			href: `/teams/${team?.id}/${page?.path}`,
		};

		return showPageBreadcrumb ? [teamCrumb, pageCrumb] : [teamCrumb];
	}, [
		page?.name,
		page?.path,
		showPageBreadcrumb,
		team?.icon,
		team?.id,
		team?.name,
	]);

	const handleNewEntity = useCallback(
		(ev: MouseEvent<HTMLButtonElement>) => {
			if (hasEntityType) {
				const teamId = currentTeamId ?? team?.id ?? '';
				if (mustQuickCreate) {
					entityModalStore.quickCreateEntity(
						page?.entityType!,
						'',
						[teamId],
						{
							owners: [user.id],
						},
						navigate,
						defaultTemplates?.results?.[0]
					);
					return;
				}

				entityModalStore.open({
					type: page?.entityType!,
					teamId: teamId || undefined,
					metadata: {
						owners: [user.id],
					},
				});
			}
		},
		[
			mustQuickCreate,
			hasEntityType,
			navigate,
			page?.entityType,
			currentTeamId,
			team?.id,
			user.id,
			defaultTemplates,
		]
	);

	const disableCreateQuestion =
		page?.entityType === EntityType.question && !questionsEnabled;

	const disableCreateBiDashboard =
		page?.entityType === EntityType.bi_dashboard && !biDashboardsEnabled;

	if (
		isNil(team) ||
		isNil(page) ||
		disableCreateQuestion ||
		disableCreateBiDashboard
	) {
		return (
			<NavBarWrapper>
				<NavBarBreadcrumbWrapper>
					<Group spacing={0} noWrap>
						<BreadcrumbsSkeleton />
					</Group>
				</NavBarBreadcrumbWrapper>
			</NavBarWrapper>
		);
	}

	const entityLabel =
		ENTITY_LABEL_OVERRIDE?.[page?.entityType ?? ''] ?? page?.entityType;

	return (
		<NavBarWrapper>
			<NavBarBreadcrumbWrapper>
				<Group spacing={0} noWrap>
					<Breadcrumbs crumbs={crumbs} />
					{!isNil(leftNode) && (
						<Group noWrap spacing={0} m={0} p={0}>
							<Text size={'sm'} color={'border/input/default'}>
								/
							</Text>
							{leftNode}
						</Group>
					)}
				</Group>
			</NavBarBreadcrumbWrapper>
			{rightNode}
			<NavBarRightSideWrapper>
				{/* {page?.entityType === EntityType.glossary &&
					aiSuggestionsEnabled &&
					canUserUpdateEntityType && (
						<ShowSuggestionsButton
							suggestionType={{
								gen_type: 'glossaries_for_workspace',
							}}
						/>
					)} */}
				{hasEntityType && withTemplate && canUserUpdateEntityType && (
					<TemplatesButtonGroup
						handleNew={handleNewEntity}
						type={page?.entityType!}
						withSetDefault
						label={`Create ${lowerCase(entityLabel)}`}
					/>
				)}
				{hasEntityType && !withTemplate && canUserUpdateEntityType && (
					<Button onClick={handleNewEntity} variant="primary">
						Create {lowerCase(entityLabel)}
					</Button>
				)}
			</NavBarRightSideWrapper>
		</NavBarWrapper>
	);
}

export default memo(TeamCatalogPageNavBar);
